import classNames from 'classnames';
import React, {ReactNode} from 'react';

import * as BenchlingUtil from 'BenchlingUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as keyboard from 'util/Keyboard';
import {BasicEvent, ElementDragEvent, ElementKeyboardEvent, ElementMouseEvent} from 'util/Types';

export enum ChipSize {
  TABLE_CELL = 'TABLE_CELL',
  MINI = 'MINI',
  SMALL = 'SMALL',
}

interface ChipContentProps {
  /** Both onAction and actionIcon need to be supplied to enable action behavior */
  actionIcon?: BenchlingIconName;
  actionTooltipText?: string;
  icon?: BenchlingIconName | null;
  /** Both onAction and actionIcon need to be supplied to enable action behavior */
  onAction?: ((e: BasicEvent) => void) | null;
  onRemove?: ((e: BasicEvent) => void) | null;
  secondaryText?: string | null;
  text: ReactNode;
}

// Note: Exported for testing Popover interactions, do not use directly.
export function ChipContent({
  actionIcon,
  actionTooltipText,
  icon,
  onAction,
  onRemove,
  secondaryText,
  text,
}: ChipContentProps): JSX.Element {
  const handleClickRemove = (e: ElementMouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    if (onRemove) {
      onRemove(e);
    }
  };

  const handleClickAction = (e: ElementMouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    if (onAction) {
      onAction(e);
    }
  };

  return (
    <span className="chip-contents" translate="no">
      {icon && <BenchlingIcon className="chip-icon" icon={icon} />}
      <span className="chip-text" {...getTestProps({component: 'Chip', element: 'chipText'})}>
        {text}
        {secondaryText && <span className="chip-text-secondaryText">{` ${secondaryText}`}</span>}
      </span>
      {onRemove && <BenchlingIcon className="chip-button" icon="cross-thick" onClick={handleClickRemove} />}
      {onAction && actionIcon && (
        <Tooltip isDisabled={actionTooltipText === ''} text={actionTooltipText}>
          <BenchlingIcon
            className="chip-action-button u-gray-darker"
            icon={actionIcon}
            onClick={handleClickAction}
            dataTestAttrs={{component: 'Chip', element: 'onActionIcon'}}
          />
        </Tooltip>
      )}
    </span>
  );
}

interface ChipToggleProps {
  children: JSX.Element;
  className?: string | null;
  draggable?: boolean;
  faded?: boolean | null;
  href?: string | null;
  icon?: BenchlingIconName | null;
  isLink: boolean;
  negative?: boolean | null;
  onBlur?: (e: BasicEvent) => void;
  onClick?: ((e: BasicEvent) => void) | null;
  onDragOver?: (e: ElementDragEvent) => void;
  onDragStart?: (e: ElementDragEvent) => void;
  onDrop?: (e: ElementDragEvent) => void;
  onFocus?: (e: BasicEvent) => void;
  onKeyDown?: (e: BasicEvent) => void;
  onRemove?: ((e: BasicEvent) => void) | null;
  size: ChipSize;
}

// Note: Exported for testing Popover interactions, do not use directly.
export function ChipToggle({
  children,
  className,
  draggable,
  faded,
  href,
  isLink,
  negative,
  onBlur,
  onClick,
  onDragOver,
  onDragStart,
  onDrop,
  onFocus,
  onKeyDown,
  onRemove,
  size,
}: ChipToggleProps): JSX.Element {
  const handleClickLink = (e: ElementMouseEvent): void => {
    // Call onClick (and ignore the default navigate to the href behavior) if it's specified
    // Check that it's a simple click, so user can still e.g., command-click the href to open in a
    // new tab
    if (BenchlingUtil.isSimpleClick(e) && onClick) {
      e.preventDefault();
      onClick(e);
    }
  };

  const handleOnKeyDown = (e: ElementKeyboardEvent): void => {
    if (keyboard.isADeleteKey(e) && onRemove) {
      onRemove(e);
    }
  };

  return (
    <span
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      className={classNames('chip', className, {
        'chip--tableCell': size === ChipSize.TABLE_CELL,
        'chip--mini': size === ChipSize.MINI,
        'chip--small': size === ChipSize.SMALL,
        'chip--faded': faded,
        'chip--negative': negative,
        'chip--isLink': isLink,
      })}
      draggable={draggable}
      onBlur={onBlur}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onFocus={onFocus}
      onKeyDown={onKeyDown ?? handleOnKeyDown}
      {...getTestProps({component: 'Chip', element: 'root'})}
    >
      {isLink ? (
        <a
          href={href || undefined}
          className="u-flex-grow"
          onClick={handleClickLink}
          target="_blank"
          rel="noreferrer noopener"
          {...getTestProps({component: 'Chip', element: 'tooltipWrapperLink'})}
        >
          {children}
        </a>
      ) : (
        children
      )}
    </span>
  );
}

export interface ChipProps {
  actionIcon?: BenchlingIconName;
  actionTooltipText?: string;
  className?: string | null;
  draggable?: boolean;
  faded?: boolean | null;
  href?: string | null;
  icon?: BenchlingIconName | null;
  isReadonly?: boolean;
  negative?: boolean | null;
  onAction?: ((e: BasicEvent) => void) | null;
  onBlur?: (e: BasicEvent) => void;
  onClick?: ((e: BasicEvent) => void) | null;
  onDragOver?: (e: ElementDragEvent) => void;
  onDragStart?: (e: ElementDragEvent) => void;
  onDrop?: (e: ElementDragEvent) => void;
  onFocus?: (e: BasicEvent) => void;
  onKeyDown?: (e: BasicEvent) => void;
  onRemove?: ((e: BasicEvent) => void) | null;
  popoverContent?: JSX.Element | null;
  popoverPlacement?: FloatingPlacement;
  /** Default false, allows the popover on hover to steal focus. */
  popoverStealFocus?: boolean;
  secondaryText?: string | null;
  size: ChipSize;
  text: ReactNode;
  tooltipTitle?: string | null;
}

/*
 * Chip component, typically used to render a reference to a Benchling item.
 *
 * Load /_design/chips for a demo of all the sizes, icon variations, and interaction options.
 */
export default function Chip({
  actionIcon,
  actionTooltipText,
  className,
  draggable,
  faded,
  href,
  icon,
  isReadonly,
  negative,
  onAction,
  onBlur,
  onClick,
  onDragOver,
  onDragStart,
  onDrop,
  onFocus,
  onKeyDown,
  onRemove,
  popoverContent,
  popoverPlacement,
  popoverStealFocus = false,
  secondaryText,
  size,
  text,
  tooltipTitle,
}: ChipProps): JSX.Element {
  const isLink = Boolean(onClick || href) && !isReadonly;

  const toggle = (
    <ChipToggle
      className={className}
      draggable={draggable}
      faded={faded}
      href={href}
      isLink={isLink}
      negative={negative}
      onBlur={onBlur}
      onClick={onClick}
      onDragOver={onDragOver}
      onDragStart={onDragStart}
      onDrop={onDrop}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onRemove={onRemove}
      size={size}
    >
      <ChipContent
        actionIcon={actionIcon}
        actionTooltipText={actionTooltipText}
        icon={icon}
        onAction={onAction}
        onRemove={onRemove}
        secondaryText={secondaryText}
        text={text}
      />
    </ChipToggle>
  );

  if (popoverContent != null) {
    return (
      <Popover
        renderContent={() => popoverContent}
        placement={popoverPlacement ?? FloatingPlacement.TOP}
        returnFocusOnClose={false}
        stealFocus={popoverStealFocus}
      >
        {toggle}
      </Popover>
    );
  } else {
    return (
      <Tooltip isDisabled={tooltipTitle === ''} text={tooltipTitle}>
        {toggle}
      </Tooltip>
    );
  }
}
